import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const WAWebhookConfig = () => {
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/wa/admin/webhook-events?type=${filterType}&limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWebhookEvents(data.events || []);
      }
    } catch (err) {
      console.error('Failed to fetch webhook events:', err);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  const eventTypeColors = {
    messages: { bg: '#dcfce7', text: '#166534' },
    message_status: { bg: '#dbeafe', text: '#1e40af' },
    message_template_status_update: { bg: '#fef3c7', text: '#92400e' },
    account_update: { bg: '#f3e8ff', text: '#6b21a8' },
    phone_number_quality_update: { bg: '#fee2e2', text: '#991b1b' },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
            <Icons.Webhook className="w-5 h-5 text-white" />
          </div>
          Webhook & Events (Admin)
        </h1>
        <p className="text-sm text-gray-500 mt-1">Monitor webhook events and message status callbacks from Meta WhatsApp API</p>
      </div>

      {/* Webhook Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Icons.Globe className="w-4 h-4" style={{ color: '#25D366' }} />
            Webhook Endpoint
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Callback URL</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.protocol}//${window.location.hostname}:8092/api/wa/webhook`}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm bg-gray-50 text-gray-700"
                />
                <button
                  onClick={() => navigator.clipboard.writeText(`${window.location.protocol}//${window.location.hostname}:8092/api/wa/webhook`)}
                  className="px-3 py-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
                >
                  <Icons.Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-xs text-blue-700">
                <strong>Subscribed Events:</strong> messages, message_status, message_template_status_update, account_update, phone_number_quality_update
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Icons.Activity className="w-4 h-4" style={{ color: '#25D366' }} />
            Event Statistics
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Total Events', value: webhookEvents.length || '0', color: '#25D366' },
              { label: 'Processed', value: webhookEvents.filter(e => e.processed).length || '0', color: '#128C7E' },
              { label: 'Pending', value: webhookEvents.filter(e => !e.processed).length || '0', color: '#F59E0B' },
              { label: 'Errors', value: webhookEvents.filter(e => e.error).length || '0', color: '#ef4444' },
            ].map((s) => (
              <div key={s.label} className="p-3 rounded-lg bg-gray-50">
                <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-[10px] text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Event Log */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Recent Webhook Events</h3>
          <div className="flex items-center gap-3">
            <div className="flex rounded-xl border border-gray-200 overflow-hidden">
              {['all', 'messages', 'message_status', 'message_template_status_update'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className="px-2.5 py-1.5 text-[11px] font-medium transition-all"
                  style={{
                    background: filterType === type ? '#25D366' : 'white',
                    color: filterType === type ? 'white' : '#6b7280',
                  }}
                >
                  {type === 'all' ? 'All' : type === 'message_template_status_update' ? 'Template' : type.replace('_', ' ')}
                </button>
              ))}
            </div>
            <button onClick={fetchEvents} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-500">
              <Icons.RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
            <p className="text-sm text-gray-500">Loading webhook events...</p>
          </div>
        ) : webhookEvents.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
              <Icons.Webhook className="w-8 h-8" style={{ color: '#25D366' }} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No Webhook Events</h3>
            <p className="text-sm text-gray-500">Webhook events will appear here once Meta starts sending callbacks.</p>
            <p className="text-xs text-gray-400 mt-2">Make sure your webhook URL is configured in the Meta App Dashboard.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 font-medium">Event Type</th>
                  <th className="text-left px-4 py-3 font-medium">WABA</th>
                  <th className="text-left px-4 py-3 font-medium">Phone Number</th>
                  <th className="text-left px-4 py-3 font-medium">Message ID</th>
                  <th className="text-center px-4 py-3 font-medium">Processed</th>
                  <th className="text-left px-4 py-3 font-medium">Error</th>
                  <th className="text-left px-4 py-3 font-medium">Time</th>
                  <th className="text-center px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {webhookEvents.map((event) => (
                  <tr key={event.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-medium" style={{
                        background: eventTypeColors[event.event_type]?.bg || '#f3f4f6',
                        color: eventTypeColors[event.event_type]?.text || '#374151',
                      }}>
                        {event.event_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">#{event.waba_account_id || 'N/A'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{event.phone_number_id || 'N/A'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">{event.wa_message_id ? event.wa_message_id.substring(0, 16) + '...' : 'N/A'}</td>
                    <td className="px-4 py-3 text-center">
                      {event.processed ? (
                        <Icons.CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                      ) : (
                        <Icons.Clock className="w-4 h-4 text-yellow-500 mx-auto" />
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-red-500 max-w-[200px] truncate">{event.error || '-'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{event.created_at || 'N/A'}</td>
                    <td className="px-4 py-3 text-center">
                      <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="View Payload">
                        <Icons.Code className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WAWebhookConfig;
